import { useContext, useState, useRef, createRef } from "react";
import { useToaster, Notification } from "rsuite";
import { UserQueryContext } from "../../../../../context/UserContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaTilsynFilterSetting,
} from "../../../../../interfaces/filters";
import {
  FirmaDtoFilterInput,
  FirmaTilsynstypeId,
} from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { AccordionFilterItem } from "../../../../accordion/Accordion";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import { ComboBox } from "../../../../filter/ComboBox";
import WarningIcon from "../../../../icons/WarningIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import { random, startCase } from "lodash";
import InputCheckbox from "@/components/formcontrols/InputCheckbox";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "tilsyn";

export default function FirmaTilsynFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);
  const { user, hasPermission } = useContext(UserQueryContext);

  // find active settings
  const settings: FirmaTilsynFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // react things
  // state
  const [quarter, setQuarter] =
    useState<FirmaTilsynFilterSetting["completedDates"][number]["quarter"]>(
      "1. kvartal",
    );

  // reffys
  const inputRef = useRef<HTMLInputElement>(null);
  const checkboxRefs = useRef(
    [...Array(Object.keys(FirmaTilsynstypeId).length)].map(() =>
      createRef<HTMLInputElement>(),
    ),
  );

  // toaster
  const toaster = useToaster();

  // permission
  if (!hasPermission(user, "TilsynRead")) {
    return null;
  }

  // helpers
  function cleanEnumString(string: string) {
    return startCase(string.toLocaleLowerCase().replaceAll("_", " "))
      .replaceAll("ae", "æ")
      .replaceAll("oe", "ø");
  }

  // helper function
  function getAndConvertStatusValues() {
    // get checkbox status things
    // get all refs
    const refValues = checkboxRefs.current.flatMap((r) => {
      if (r.current) {
        if (r.current.checked) {
          return r.current?.id;
        } else {
          return [];
        }
      } else {
        return [];
      }
    });

    // remove id's from values
    const statusValues: Array<FirmaTilsynstypeId> = refValues.map(
      (val) => val.substring(0, val.indexOf("__")) as FirmaTilsynstypeId,
    );

    return statusValues;
  }

  function buildFilter(settings: FirmaTilsynFilterSetting) {
    return f<FirmaDtoFilterInput>()
      .addFilter("firmaTilsyn", {
        some: {
          and: [
            settings.type.length
              ? {
                  firmaTilsynstype: {
                    in: settings.type,
                  },
                }
              : {},

            settings.completedDates.length
              ? {
                  or: settings.completedDates.map((setting) => {
                    return {
                      gennemfoerselDato:
                        setting.quarter !== "Hele"
                          ? {
                              eq:
                                setting.quarter === "1. kvartal"
                                  ? `${setting.completedYear}-01-01`
                                  : setting.quarter === "2. kvartal"
                                    ? `${setting.completedYear}-04-01`
                                    : setting.quarter === "3. kvartal"
                                      ? `${setting.completedYear}-07-01`
                                      : `${setting.completedYear}-10-01`,
                            }
                          : {
                              gte: `${setting.completedYear}-01-01`,
                              lte: `${setting.completedYear}-12-31`,
                            },
                    };
                  }),
                }
              : {},
          ],
        },
      })
      .build();
  }

  // handlers
  function updateFilterHandler(eventType: "type" | "interval") {
    // get values
    const inputValue = inputRef.current?.value;
    const statusValues = getAndConvertStatusValues();
    const completedDates = settings?.completedDates || [];

    if (eventType === "interval") {
      if (!inputValue || inputValue.length !== 4) {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              {`Ugyldigt årstal. Gyldigt format er f.eks. - '${new Date().getFullYear()}'`}
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 4000,
          },
        );
        return;
      }

      const settingsItem: FirmaTilsynFilterSetting["completedDates"][number] = {
        id: `${quarter}-${inputValue}`,
        quarter: quarter,
        completedYear: inputValue,
      };

      const filterExist = completedDates?.some(
        (item) => item.id === settingsItem.id,
      );

      if (filterExist) {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              Filteret er allerede tilføjet.
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 2500,
          },
        );
        return;
      } else {
        completedDates.push(settingsItem);
      }
    }

    const updatedSettings: FirmaTilsynFilterSetting = {
      completedDates: completedDates,
      type: statusValues,
    };

    const canBuildFilter =
      updatedSettings.completedDates.length || updatedSettings.type.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    // update the query filter
    // update the query filter
    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaTilsynFilterSetting> =
      {
        filterKey: filterKey,
        filter: filterString,
        parentFilter: "and",
        settings: updatedSettings,
      };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  function removeFilterHandler(id: string) {
    const statusValues = getAndConvertStatusValues();

    const updatedSettings: FirmaTilsynFilterSetting = {
      completedDates: settings?.completedDates.filter((item) => item.id !== id),
      type: statusValues,
    };

    const canBuildFilter =
      updatedSettings.completedDates.length || updatedSettings.type.length;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    // update the query filter
    const queryFilter: QueryFilter<FirmaFilterKeys, FirmaTilsynFilterSetting> =
      {
        filterKey: filterKey,
        filter: filterString,
        parentFilter: "and",
        settings: updatedSettings,
      };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Gennemførte tilsyn"
      value="gennemfoerte-tilsyn"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <h4 className="h6 mb-1 text-secondary">Type</h4>
      {Object.entries(FirmaTilsynstypeId)
        .sort()
        .map(([key, value], index) => {
          let displayName = startCase(
            value.replaceAll("_", " ").toLocaleLowerCase(),
          );

          // æ, ø og å'ify the enum
          displayName = displayName.replaceAll("ae", "æ").replaceAll("oe", "ø");

          return (
            <InputCheckbox
              id={`${value}__${random(0, 1000)}`}
              label={displayName}
              ref={checkboxRefs.current[index]}
              checked={settings?.type.includes(value)}
              onChange={() => updateFilterHandler("type")}
              key={key}
            />
          );
        })}

      <h4 className="h6 mt-3  mb-1 text-secondary">Gennemført</h4>

      {settings?.completedDates && (
        <div className="c-applied-filter-group">
          {settings?.completedDates?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              labelPrefix={`${filter.quarter}`}
              label={cleanEnumString(filter.completedYear)}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <ComboBox>
        <Select
          defaultValue={quarter}
          onValueChange={(value: string) => {
            if (
              value === "Hele" ||
              value === "1. kvartal" ||
              value === "2. kvartal" ||
              value === "3. kvartal" ||
              value === "4. kvartal"
            ) {
              setQuarter(value);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg interval" badge />
          <SelectContent small>
            <SelectItem value={"Hele"} key={"Hele"}>
              Hele
            </SelectItem>
            <SelectItem value={"1. kvartal"} key={"1. kvartal"}>
              1. kvartal
            </SelectItem>
            <SelectItem value={"2. kvartal"} key={"2. kvartal"}>
              2. kvartal
            </SelectItem>
            <SelectItem value={"3. kvartal"} key={"3. kvartal"}>
              3. kvartal
            </SelectItem>
            <SelectItem value={"4. kvartal"} key={"4. kvartal"}>
              4. kvartal
            </SelectItem>
          </SelectContent>
        </Select>

        <input
          className="form-control"
          type="number"
          placeholder={`F.eks. '${new Date().getFullYear().toString()}'`}
          ref={inputRef}
          required
          onKeyUp={(event) => {
            if (event.key === "Enter" && inputRef.current?.value) {
              updateFilterHandler("interval");
            }
          }}
        />

        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            updateFilterHandler("interval");
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
